/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Auth client type filter (mcp:auth only).
 */
export const APIKeyPolicySelectorClientType = {
  ServiceAccount: "service_account",
  StaticCredential: "static_credential",
  PassThrough: "pass_through",
  Ema: "ema",
  Wildcard: "*",
} as const;
/**
 * Auth client type filter (mcp:auth only).
 */
export type APIKeyPolicySelectorClientType = OpenEnum<
  typeof APIKeyPolicySelectorClientType
>;

/**
 * mcp for mcp:connect grants, mcp_auth_client for mcp:auth grants
 */
export const APIKeyPolicySelectorResourceKind = {
  Mcp: "mcp",
  McpAuthClient: "mcp_auth_client",
} as const;
/**
 * mcp for mcp:connect grants, mcp_auth_client for mcp:auth grants
 */
export type APIKeyPolicySelectorResourceKind = OpenEnum<
  typeof APIKeyPolicySelectorResourceKind
>;

/**
 * What an API key policy grant covers: servers (mcp) or auth clients (mcp_auth_client).
 */
export type APIKeyPolicySelector = {
  /**
   * Auth client type filter (mcp:auth only).
   */
  clientType?: APIKeyPolicySelectorClientType | undefined;
  /**
   * The project the servers are in; required when resource_id is '*', and the key's own project when it is bound to one.
   */
  projectId?: string | undefined;
  /**
   * A server or auth client ID, or '*' for every server in project_id. mcp:auth grants must name their client.
   */
  resourceId: string;
  /**
   * mcp for mcp:connect grants, mcp_auth_client for mcp:auth grants
   */
  resourceKind: APIKeyPolicySelectorResourceKind;
};

/** @internal */
export const APIKeyPolicySelectorClientType$inboundSchema: z.ZodMiniType<
  APIKeyPolicySelectorClientType,
  unknown
> = openEnums.inboundSchema(APIKeyPolicySelectorClientType);
/** @internal */
export const APIKeyPolicySelectorClientType$outboundSchema: z.ZodMiniType<
  string,
  APIKeyPolicySelectorClientType
> = openEnums.outboundSchema(APIKeyPolicySelectorClientType);

/** @internal */
export const APIKeyPolicySelectorResourceKind$inboundSchema: z.ZodMiniType<
  APIKeyPolicySelectorResourceKind,
  unknown
> = openEnums.inboundSchema(APIKeyPolicySelectorResourceKind);
/** @internal */
export const APIKeyPolicySelectorResourceKind$outboundSchema: z.ZodMiniType<
  string,
  APIKeyPolicySelectorResourceKind
> = openEnums.outboundSchema(APIKeyPolicySelectorResourceKind);

/** @internal */
export const APIKeyPolicySelector$inboundSchema: z.ZodMiniType<
  APIKeyPolicySelector,
  unknown
> = z.pipe(
  z.object({
    client_type: z.optional(APIKeyPolicySelectorClientType$inboundSchema),
    project_id: z.optional(z.string()),
    resource_id: z.string(),
    resource_kind: APIKeyPolicySelectorResourceKind$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "client_type": "clientType",
      "project_id": "projectId",
      "resource_id": "resourceId",
      "resource_kind": "resourceKind",
    });
  }),
);
/** @internal */
export type APIKeyPolicySelector$Outbound = {
  client_type?: string | undefined;
  project_id?: string | undefined;
  resource_id: string;
  resource_kind: string;
};

/** @internal */
export const APIKeyPolicySelector$outboundSchema: z.ZodMiniType<
  APIKeyPolicySelector$Outbound,
  APIKeyPolicySelector
> = z.pipe(
  z.object({
    clientType: z.optional(APIKeyPolicySelectorClientType$outboundSchema),
    projectId: z.optional(z.string()),
    resourceId: z.string(),
    resourceKind: APIKeyPolicySelectorResourceKind$outboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      clientType: "client_type",
      projectId: "project_id",
      resourceId: "resource_id",
      resourceKind: "resource_kind",
    });
  }),
);

export function apiKeyPolicySelectorToJSON(
  apiKeyPolicySelector: APIKeyPolicySelector,
): string {
  return JSON.stringify(
    APIKeyPolicySelector$outboundSchema.parse(apiKeyPolicySelector),
  );
}
export function apiKeyPolicySelectorFromJSON(
  jsonString: string,
): SafeParseResult<APIKeyPolicySelector, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => APIKeyPolicySelector$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'APIKeyPolicySelector' from JSON`,
  );
}
